import { responsive, useTheme } from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useMemo } from 'react';

import { getContainerStyles } from '@/utils';

export const useStyles = (isDark: boolean) => {
  const { theme } = useTheme();

  return useMemo(
    () => ({
      wrapper: css`
        width: 100%;
      `,

      container: css`
        ${getContainerStyles(theme)};
        position: relative;

        ${responsive.create({
          paddingTop: {
            default: theme.spacing.get(56),
            md: theme.spacing.get(72),
            lg: theme.spacing.get(100),
          },
          paddingBottom: {
            default: theme.spacing.get(60),
            md: theme.spacing.get(80),
            lg: theme.spacing.get(90),
          },
        })}
      `,

      titleChipContainer: css`
        --chip-bg-color: ${isDark ? '#b7f0ef33' : '#ECEEFA'};
        border: none;
        color: ${
          isDark
            ? theme.colors.brandColors.secondary?.default
            : theme.colors.brandColors.primary?.default
        };

        ${responsive.create({
          padding: {
            default: `${theme.spacing.get(8)} ${theme.spacing.get(8)}`,
            md: `${theme.spacing.get(8)} ${theme.spacing.get(10)}`,
            lg: `${theme.spacing.get(10)} ${theme.spacing.get(12)}`,
          },
          marginBottom: {
            default: theme.spacing.get(24),
            md: theme.spacing.get(24),
            lg: theme.spacing.get(32),
          },
        })}
      `,

      titleChipLabel: css`
        font-weight: 600;

        ${responsive.create({
          fontSize: {
            default: theme.fonts.size.xxs,
            lg: theme.fonts.size.sm,
          },
        })}
      `,

      title: css`
        font-weight: ${theme.fonts.weight.medium};
        color: var(--text-color);
        text-align: center;

        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.xl}`,
            md: `${theme.fonts.size.xxl}`,
            lg: `${theme.fonts.size['4xl']}`,
          },
        })}
      `,

      stepsWrapper: css`
        ${responsive.create({
          height: {
            md: '408px',
            lg: '630px',
          },
        })}
      `,

      mediaSlot: css`
        border: 1px solid ${isDark ? '#424242' : '#E0E0E0'};
        border-radius: ${theme.border.radius.xxl};
      `,

      mobileStackItem: css`
        padding: ${theme.spacing.get(16)};
        border-radius: 20px;
        border: 1px solid ${isDark ? '#424242' : '#E0E0E0'};
      `,

      stepsContainer: css`
        ${responsive.create({
          paddingLeft: {
            default: 0,
            xl: theme.spacing.get(120),
            ultra_large: theme.spacing.get(300),
          },
          paddingRight: {
            default: 0,
            xl: theme.spacing.get(120),
            ultra_large: theme.spacing.get(300),
          },
        })}
      `,

      headerSection: css`
        ${responsive.create({
          marginBottom: {
            default: theme.spacing.get(36),
            md: theme.spacing.get(64),
            lg: theme.spacing.get(72), // because dynamic height is taking some space approx 16px ~ 72+ 16 = 88px
          },
        })}
      `,

      description: css`
        color: ${theme.colors.commonColors.white};
        max-width: 680px;
        text-align: center;
        color: var(--desc-text-color);
        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.sm}`,
            md: `${theme.fonts.size.md}`,
            lg: `${theme.fonts.size.xl}`,
          },
          maxWidth: {
            default: '300px',
            md: '600px',
          },
        })}
      `,

      label: css`
        color: var(--heading-text-color);

        ${responsive.create({
          fontSize: {
            md: '16px',
            lg: '24px',
          },
        })}
      `,

      hoverIndicator: css`
        --steps-indicator-bg-color: linear-gradient(235deg, #4054d1 -1.18%, #17ceca 112.11%);
      `,

      twoColumnLayout: css`
        width: 100%;
      `,

      leftColumn: css`
        flex: 0.55;
        min-width: 0;
      `,

      rightColumn: css`
        flex: 0.45;
        min-width: 0;
      `,

      desktopContentWrapper: css`
        width: 100%;
        min-width: 0;

        ${responsive.create({
          display: {
            default: 'block',
            md: 'flex',
          },
        })}

        @media (min-width: 768px) {
          align-items: flex-start;
        }

        > * {
          width: 100%;
        }
      `,

      /*
       * Used ONLY for calculating the tallest content.
       * It is completely outside the viewport.
       */
      measurementContainer: css`
        position: fixed;
        top: 0;
        left: -10000px;

        width: 50%;

        opacity: 0;
        pointer-events: none;
        user-select: none;

        overflow: visible;

        z-index: -9999;

        @media (max-width: 767px) {
          display: none;
        }
      `,

      measurementItem: css`
        width: 100%;
        min-width: 0;
        height: auto;
        overflow: visible;
      `,

      sectionTitle: css`
        color: var(--heading-text-color);
        font-weight: ${theme.fonts.weight.semiBold};

        ${responsive.create({
          fontSize: {
            md: theme.fonts.size.xs,
            lg: theme.fonts.size.lg,
          },
          marginBottom: {
            md: theme.spacing.get(8),
            lg: theme.spacing.get(12),
          },
        })}
      `,

      bulletRow: css`
        display: flex;
        align-items: flex-start;
        gap: ${theme.spacing.get(8)};
      `,

      bulletText: css`
        color: ${isDark ? '#9E9E9E' : '#616161'};
        ${responsive.create({
          fontSize: {
            md: '12px',
            lg: '18px',
          },
        })}
      `,

      sectionDescription: css`
        color: ${isDark ? '#9E9E9E' : '#616161'};

        ${responsive.create({
          fontSize: {
            md: '12px',
            lg: '18px',
          },
        })};
      `,

      mobileLabel: css`
        font-size: ${theme.fonts.size.lg};
        font-weight: 600;
        margin-bottom: 4px;
        color: ${isDark ? '#EEEEEE' : '#212121'};
      `,

      mobileCommonText: css`
        color: ${isDark ? '#9E9E9E' : '#616161'};
      `,

      bulletPointText: css`
        flex: 1;
        color: ${isDark ? '#9E9E9E' : '#616161'};
      `,

      mobileWhyWciFitsHeader: css`
        font-weight: 600;
        font-size: ${theme.fonts.size.lg};
        margin-bottom: 12px;
        margin-top: 20px;
        color: ${isDark ? '#F5F5F5' : '#212121'};
      `,

      mobileWhyWciFitsDescription: css`
        font-weight: 400;
        color: ${isDark ? '#9E9E9E' : '#616161'};
      `,

      mobilePainToday: css`
        font-weight: 600;
        font-size: ${theme.fonts.size.lg};
        margin-bottom: 12px;
        margin-top: 20px;
        color: ${isDark ? '#F5F5F5' : '#212121'};
      `,

      stableImageFrame: css`
        width: 100%;
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
        border-radius: 20px;
        background: ${isDark ? '#1f1f1f' : '#f2f2f2'};
        transform: translateZ(0);
        backface-visibility: hidden;

        ${responsive.create({
          width: {
            xl: '100%',
            lg: '100%',
            md: '100%',
            sm: '100%',
            xxs: '100%',
          },
          height: {
            xl: 364,
            lg: 258,
            md: 258,
            sm: 220,
            default: 190,
          },
        })}
      `,

      imageWrapper: css`
        width: 100%;
        max-width: 100%;
        min-width: 0;
        display: flex;
        justify-content: center;
        overflow: hidden;
      `,
    }),
    [theme, isDark],
  );
};
